import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRightLong,
  faCircleCheck,
  faCircleExclamation,
  faSpinner,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';
import { PLATFORM_ROUTES } from '@/routes/platform-routes';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import { canPlatform } from '@/lib/platform-permissions';
import { PlanMigrationForm } from './components/PlanMigrationForm';
import {
  listProviderMigrations,
  type ProviderMigrationListItemDto,
} from './api/provider-migrations.api';

/**
 * Provider migrations — READ-ONLY list.
 *
 * Exists so the detail view can be reached without hardcoding a migration id.
 * Deliberately distinct from the Migration Center, which serves LOCAL PATH
 * migrations (OQ-AME-1 keeps the two apart).
 *
 * The ONLY action here is "Plan Migration", which creates a DRY RUN and copies
 * nothing. No control on THIS page can start, approve, pause, cancel, roll back,
 * flip ownership, clean up or delete anything.
 *
 * The later steps — approve, run, ownership transfer, finalize — each live on
 * the DETAIL page, behind their own explicit confirmation, so that every one of
 * them is taken against a single migration the operator is actually looking at.
 * Cleanup, source deletion, purge, provider deactivation and ownership rollback
 * remain unreachable from the browser entirely: the API client has no wrapper
 * for any of them.
 */

const STATUS_TONE: Record<string, string> = {
  COMPLETED: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  VERIFYING: 'bg-sky-500/10 text-sky-600 border-sky-500/30',
  RUNNING: 'bg-primary/10 text-primary border-primary/30',
  READY: 'bg-sky-500/10 text-sky-600 border-sky-500/30',
  PAUSED: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  FAILED: 'bg-destructive/10 text-destructive border-destructive/30',
  ROLLED_BACK: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
};

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

export function ProviderMigrationsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ProviderMigrationListItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [planning, setPlanning] = useState(false);

  // Planning WRITES (it creates a migration row), so it is gated on
  // `platform:system:manage` to match the endpoint. This is a UI affordance;
  // the server's @RequirePlatformPermissions is the actual enforcement.
  const platformRole = usePlatformAuthStore((s) => s.user?.role);
  const canPlan = useMemo(
    () => canPlatform(platformRole, 'platform:system:manage'),
    [platformRole],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await listProviderMigrations());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load provider migrations.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6 p-6" data-testid="provider-migrations-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Provider Migrations</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Provider-to-provider object migrations. Planning produces a dry run; each later step is
            confirmed individually on the migration&apos;s own page.
          </p>
        </div>
        {canPlan && !planning && (
          <button
            type="button"
            data-testid="plan-migration-action"
            onClick={() => setPlanning(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
            Plan Migration
          </button>
        )}
      </div>

      {planning && (
        <PlanMigrationForm
          onPlanned={(migrationId) => {
            setPlanning(false);
            navigate(`${PLATFORM_ROUTES.STORAGE_PROVIDER_MIGRATIONS}/${migrationId}`);
          }}
          onCancel={() => setPlanning(false)}
        />
      )}

      {loading ? (
        <div
          className="flex items-center gap-3 text-sm text-muted-foreground"
          data-testid="list-loading"
        >
          <FontAwesomeIcon icon={faSpinner} spin className="h-4 w-4" />
          Loading migrations…
        </div>
      ) : error ? (
        <div
          className="rounded-xl border border-destructive/30 bg-destructive/5 p-4"
          data-testid="list-error"
        >
          <p className="flex items-center gap-2 text-sm font-medium text-destructive">
            <FontAwesomeIcon icon={faCircleExclamation} className="h-4 w-4" />
            {error}
          </p>
        </div>
      ) : rows.length === 0 ? (
        <div
          className="rounded-xl border border-border bg-card p-8 text-center"
          data-testid="list-empty"
        >
          <p className="text-sm text-muted-foreground">No provider migrations have been created.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-3 font-medium">Migration</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 text-right font-medium">Objects</th>
                <th className="px-4 py-3 text-right font-medium">Verified</th>
                <th className="px-4 py-3 text-right font-medium">Failed</th>
                <th className="px-4 py-3 font-medium">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((m) => (
                <tr key={m.id} className="hover:bg-muted/40">
                  <td className="px-4 py-3">
                    <Link
                      to={`${PLATFORM_ROUTES.STORAGE_PROVIDER_MIGRATIONS}/${m.id}`}
                      className="font-medium text-primary hover:underline"
                    >
                      <span className="inline-flex items-center gap-2">
                        View migration
                        <FontAwesomeIcon icon={faArrowRightLong} className="h-3 w-3" />
                      </span>
                    </Link>
                    <span className="block font-mono text-xs text-muted-foreground">{m.id}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold',
                        STATUS_TONE[m.status] ?? 'bg-muted text-muted-foreground border-border',
                      )}
                    >
                      {m.status === 'COMPLETED' && (
                        <FontAwesomeIcon icon={faCircleCheck} className="h-3 w-3" />
                      )}
                      {m.status.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-foreground">
                    {(m.totalObjects ?? 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-foreground">
                    {m.verifiedObjects.toLocaleString()}
                  </td>
                  <td
                    className={cn(
                      'px-4 py-3 text-right tabular-nums',
                      m.failedObjects > 0 ? 'text-amber-600' : 'text-muted-foreground',
                    )}
                  >
                    {m.failedObjects.toLocaleString()}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-muted-foreground">
                    {fmtDateTime(m.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
